// src/app/components/chat/MessageBubble.jsx
import React from "react";
import {
  Download,
  File,
  Volume2,
  RefreshCw,
  CheckCheck,
  Clock,
  AlertCircle,
} from "lucide-react";

export default function MessageBubble({ msg, onRetry }) {
  const isMe = msg.sender === "ME";
  const isText = msg.kind === "text";
  const isGif = msg.kind === "gif";
  const isFile = msg.kind === "file";
  const isVoice = msg.kind === "voice";

  const status = msg.status || "sent";
  const isPending = status === "pending";
  const isFailed = status === "failed";
  const isSent = status === "sent";
  const isDelivered = status === "delivered";
  const isRead = status === "read";

  const timestamp =
    msg.createdAtLabel ||
    (msg.createdAt
      ? new Intl.DateTimeFormat("en", {
          hour: "2-digit",
          minute: "2-digit",
        }).format(new Date(msg.createdAt))
      : "");

  return (
    <div className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[70%] ${
          isMe
            ? "bg-gradient-to-r from-primary-600 to-primary-500"
            : "bg-gray-800"
        } rounded-2xl px-4 py-3 shadow-lg`}
      >
        {/* Sender Label (for moderator messages) */}
        {!isMe && (
          <div className="text-xs text-primary-400 font-semibold mb-1">
            Moderator
          </div>
        )}

        {/* Text Message */}
        {isText && (
          <div className="text-white whitespace-pre-wrap break-words">
            {msg.text}
          </div>
        )}

        {/* GIF Message */}
        {isGif && msg.gifUrl && (
          <div className="rounded-lg overflow-hidden">
            <img
              src={msg.gifUrl}
              alt="GIF"
              className="max-w-full max-h-64 object-contain"
            />
          </div>
        )}

        {/* File Message */}
        {isFile && (
          <a
            href={msg.fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-3 bg-black/20 rounded-lg hover:bg-black/30 transition-colors"
          >
            <div className="h-10 w-10 rounded-lg bg-white/10 flex items-center justify-center">
              <File size={20} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-white truncate">
                {msg.fileName || "File"}
              </div>
              <div className="text-xs text-gray-300">
                {msg.size ? `${(msg.size / 1024).toFixed(1)} KB` : "Unknown size"}
              </div>
            </div>
            <Download size={18} className="text-white flex-shrink-0" />
          </a>
        )}

        {/* Voice Message */}
        {isVoice && msg.voiceUrl && (
          <div className="flex items-center gap-3">
            <audio controls className="max-w-full">
              <source src={msg.voiceUrl} type="audio/webm" />
              Your browser does not support audio playback.
            </audio>
          </div>
        )}

        {/* Footer: Timestamp + Status */}
        <div className="flex items-center justify-between mt-2 gap-3">
          <div className="text-xs text-gray-300">{timestamp}</div>

          <div className="flex items-center gap-1">
            {/* Status Icons (for own messages) */}
            {isMe && (
              <>
                {isPending && (
                  <Clock size={14} className="text-gray-400 animate-pulse" />
                )}
                {isFailed && (
                  <button
                    onClick={() => onRetry?.(msg)}
                    className="flex items-center gap-1 text-red-400 hover:text-red-300 transition-colors"
                    title="Click to retry"
                  >
                    <AlertCircle size={14} />
                    <RefreshCw size={12} />
                  </button>
                )}
                {isSent && <CheckCheck size={14} className="text-gray-400" />}
                {isDelivered && (
                  <CheckCheck size={14} className="text-gray-300" />
                )}
                {isRead && <CheckCheck size={14} className="text-primary-300" />}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
